import AddIcon from '@mui/icons-material/Add'
import CancelIcon from '@mui/icons-material/Cancel'
import CheckIcon from '@mui/icons-material/Check'
import EventAvailableIcon from '@mui/icons-material/EventAvailable'
import LockIcon from '@mui/icons-material/Lock'
import UpdateIcon from '@mui/icons-material/Update'
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid2 as Grid,
  List,
  ListItem,
  ListItemText,
  Radio,
  RadioGroup,
  Switch,
  Typography,
} from '@mui/material'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker'
import dayjs from 'dayjs'
import { useAtom } from 'jotai'
import React from 'react'

import PrivateSessionForm from './PrivateSessionForm'
import privateSessionCreateStateAtom from '../../contexts/AtomPrivateSessions'
import { STATUS } from '../../share/Constant'
import { formatFullDate, formatToDate } from '../../utils/TimeUtility'
import ConfirmationDialog from '../dialog/ConfirmationDialog'
import { usePrivateSessionCreation, usePrivateSessionCreationHandleConfirmSubmit } from '../hooks/usePrivateSessionCreation'

// Status icons to display a diff
const STATUS_ICONS = {
  ADD: (props) => (
    <AddIcon
      sx={{
        fontSize: '1.2rem',
        color: 'grey.400',
        ...props?.sx,
      }}
    />
  ),
  AVAILABLE: (props) => (
    <CheckIcon
      sx={{
        fontSize: '1.2rem',
        color: '#4caf50',
        ...props?.sx,
      }}
    />
  ),
  LOCKED: (props) => (
    <LockIcon
      sx={{
        fontSize: '1.2rem',
        color: 'grey.400',
        ...props?.sx,
      }}
    />
  ),
  BOOKED: (props) => (
    <EventAvailableIcon
      sx={{
        fontSize: '1.2rem',
        color: 'grey.400',
        ...props?.sx,
      }}
    />
  ),
  UPDATE: (props) => (
    <UpdateIcon
      sx={{
        fontSize: '1.2rem',
        color: 'primary.main',
        ...props?.sx,
      }}
    />
  ),
  DELETE: (props) => (
    <CancelIcon
      sx={{
        fontSize: '1.2rem',
        color: 'error.main',
        ...props?.sx,
      }}
    />
  ),
}

/**
 * Time Slot Item Component
 * Displays a single time slot with status indicator
 */
const TimeSlotItem = ({ timeSlot, details = false, xs = 6, sm = 4, md = 3, icon }) => {
  let TimeSlotStatusIcon
  if (timeSlot.status === STATUS.BOOKED) {
    TimeSlotStatusIcon = STATUS_ICONS.BOOKED
  } else if (timeSlot.status === STATUS.LOCKED) {
    TimeSlotStatusIcon = STATUS_ICONS.LOCKED
  } else {
    TimeSlotStatusIcon = STATUS_ICONS.AVAILABLE
  }

  const StatusIcon = icon || TimeSlotStatusIcon

  return (
    <Grid size={{ xs, sm, md }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          p: 1,
          borderRadius: 1,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: timeSlot.status === STATUS.AVAILABLE ? 'success.light' : 'grey.300',
        }}
      >
        <Box sx={{ mr: 1, display: 'flex', color: timeSlot.status === STATUS.AVAILABLE ? 'success.main' : 'grey.500' }}>
          <StatusIcon />
        </Box>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="body2" component="div" sx={{ fontWeight: 'medium' }}>
            {timeSlot.label}
          </Typography>
          {details && (
            <Typography variant="caption" color="text.secondary">
              {timeSlot.status === STATUS.AVAILABLE ? 'Available' : 'Booked'}
            </Typography>
          )}
        </Box>
      </Box>
    </Grid>
  )
}

const SessionItem = ({ Session, status, icon, xs = 6, sm = 4, md = 3 }) => {
  const timeSlot = {
    label: `${dayjs(Session.startTime).format('HH:mm')} - ${dayjs(Session.endTime).format('HH:mm')}`,
    status: status,
  }
  return <TimeSlotItem timeSlot={timeSlot} details={false} xs={xs} sm={sm} md={md} icon={icon} />
}

const ModifyDialog = () => {
  const { handleConfirmSubmit } = usePrivateSessionCreationHandleConfirmSubmit()
  const [formData] = useAtom(privateSessionCreateStateAtom.form)
  const [sessionsChanges] = useAtom(privateSessionCreateStateAtom.sessionsChanges)
  const [isSubmitting] = useAtom(privateSessionCreateStateAtom.submit)
  const [date] = useAtom(privateSessionCreateStateAtom.date)
  const [dialog, setDialog] = useAtom(privateSessionCreateStateAtom.dialog)

  const handleClose = () => {
    setDialog(false)
  }

  const handleConfirmClick = () => {
    if (handleConfirmSubmit()) {
      handleClose()
    }
  }
  if (!dialog) return null

  return (
    <ConfirmationDialog
      open={dialog}
      onClose={handleClose}
      onConfirm={handleConfirmClick}
      title="Confirm Session Creation"
      content=""
      maxWidth="md"
      actions={{
        cancel: {
          label: 'Cancel',
          onClick: handleClose,
          color: 'inherit',
        },
        confirm: {
          label: isSubmitting ? 'Saving...' : 'Save Change',
          onClick: handleConfirmClick,
          color: 'primary',
          variant: 'contained',
          disabled: isSubmitting || !sessionsChanges.isChanged,
          submitting: isSubmitting,
        },
      }}
    >
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          Session Details:
        </Typography>
        <List dense>
          <ListItem>
            <ListItemText primary={`Sport Type: ${formData.sport === 'SKI' ? 'Ski' : 'Snowboard'}`} />
          </ListItem>
          <ListItem>
            <ListItemText primary={`Location: ${formData.skiResort}`} />
          </ListItem>
          <ListItem>
            <ListItemText primary={`Price: $${formData.price}`} />
          </ListItem>
        </List>
      </Box>

      {sessionsChanges && (
        <>
          <Divider sx={{ mb: 2 }} />

          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Your available time slots for {formatFullDate(date)}:
          </Typography>
          <Box sx={{ mb: 3 }}>
            <Grid container spacing={1}>
              {sessionsChanges.openTimeSlots.map((timeSlot) => (
                <TimeSlotItem key={timeSlot.value} timeSlot={timeSlot} details={true} />
              ))}
            </Grid>
          </Box>

          {sessionsChanges.sessionsToDelete.length > 0 && (
            <>
              <Typography variant="subtitle1" fontWeight="bold" color="error" gutterBottom>
                Following sessions will be deleted:
              </Typography>
              <Grid container spacing={1} sx={{ mb: 3 }}>
                {sessionsChanges.sessionsToDelete.map((session) => (
                  <SessionItem key={session.resourceId} Session={session} status={STATUS.CANCELLED} icon={STATUS_ICONS.DELETE} />
                ))}
              </Grid>
            </>
          )}

          {sessionsChanges.sessionsToUpdate.length > 0 && (
            <>
              <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
                Following sessions will be updated:
              </Typography>
              <Grid container spacing={1} sx={{ mb: 3 }}>
                {sessionsChanges.sessionsToUpdate.map((session) => (
                  <SessionItem key={session.resourceId} Session={session} status={STATUS.AVAILABLE} icon={STATUS_ICONS.UPDATE} />
                ))}
              </Grid>
            </>
          )}

          {sessionsChanges.sessionsToCreate.length > 0 && (
            <>
              <Typography variant="subtitle1" fontWeight="bold" color="success.main" gutterBottom>
                New sessions to be created:
              </Typography>
              <Grid container spacing={1} sx={{ mb: 3 }}>
                {sessionsChanges.sessionsToCreate.map((session, index) => (
                  <SessionItem key={`${date}-${index}`} Session={session} status={STATUS.AVAILABLE} icon={STATUS_ICONS.ADD} />
                ))}
              </Grid>
            </>
          )}
          {!sessionsChanges.isChanged && (
            <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
              No changes to be made
            </Typography>
          )}
        </>
      )}
    </ConfirmationDialog>
  )
}

const CleanPrivateSessionForm = () => {
  const [formData, setFormData] = useAtom(privateSessionCreateStateAtom.form)
  const [formErrors] = useAtom(privateSessionCreateStateAtom.formError)
  const [template] = useAtom(privateSessionCreateStateAtom.template)

  return <PrivateSessionForm formData={formData} formErrors={formErrors} onFormChange={setFormData} template={template} />
}

const TimeSlotManagement = () => {
  const [, setDate] = useAtom(privateSessionCreateStateAtom.date)
  const [timeSlotOptions, setTimeSlotOptions] = useAtom(privateSessionCreateStateAtom.timeSlotOptions)
  const { handleSubmit, isLoading, toggleTimeSlot } = usePrivateSessionCreation()
  const [timeSlots] = useAtom(privateSessionCreateStateAtom.timeSlots)
  const [, setDialog] = useAtom(privateSessionCreateStateAtom.dialog)
  const [isSubmitting] = useAtom(privateSessionCreateStateAtom.submit)

  const handleSubmitClick = (e) => {
    if (handleSubmit(e)) {
      setDialog(true)
    }
  }

  return (
    <Card>
      <CardContent>
        {/* Date Picker Calendar */}
        <Grid>
          <Card>
            <CardContent>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <StaticDatePicker displayStaticWrapperAs="desktop" onChange={(newValue) => setDate(formatToDate(newValue))} disablePast />
              </LocalizationProvider>
            </CardContent>
          </Card>
        </Grid>

        {/* Time Slots Selection */}
        <Grid size={{ xs: 12 }} alignItems="center" justifyContent="center">
          <Divider sx={{ my: 2 }}>Manage Your Availability</Divider>

          <Typography variant="body2" color="text.secondary">
            Click on time slots to mark them as available or unavailable
          </Typography>

          {/* Time Slot Configuration */}
          <Box sx={{ mb: 2, mt: 2 }}>
            <Grid container spacing={2} alignItems="center" justifyContent="center">
              <Grid size={{ xs: 12, sm: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={timeSlotOptions.halfhour}
                      onChange={(e) =>
                        setTimeSlotOptions({
                          ...timeSlotOptions,
                          halfhour: e.target.checked,
                        })
                      }
                      color="primary"
                    />
                  }
                  labelPlacement="top"
                  label={timeSlotOptions.halfhour ? 'Half-Hour' : 'Even Hour'}
                  sx={{ margin: 0 }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 9 }}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">Session Duration</FormLabel>
                  <RadioGroup
                    row
                    value={timeSlotOptions.sessionDuration}
                    onChange={(e) =>
                      setTimeSlotOptions({
                        ...timeSlotOptions,
                        sessionDuration: parseInt(e.target.value, 10),
                      })
                    }
                  >
                    <FormControlLabel value={60} control={<Radio />} label="1 Hour" />
                    <FormControlLabel value={120} control={<Radio />} label="2 Hours" />
                    <FormControlLabel value={180} control={<Radio />} label="3 Hours" />
                  </RadioGroup>
                </FormControl>
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ my: 2 }} />

          <FormControl fullWidth>
            <Grid container spacing={1} alignItems="center" justifyContent="center">
              {isLoading ? (
                <CircularProgress />
              ) : (
                timeSlots.map((timeSlot) => (
                  <Grid key={timeSlot.value}>
                    <Button
                      fullWidth
                      variant={timeSlot.status === STATUS.AVAILABLE ? 'contained' : 'outlined'}
                      onClick={() => toggleTimeSlot(timeSlot)}
                      disabled={timeSlot.status === STATUS.BOOKED || timeSlot.status === STATUS.LOCKED}
                      sx={{
                        borderRadius: 2,
                        py: 1,
                        textTransform: 'none',
                        position: 'relative',
                        px: 2,
                        pr: 1,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {timeSlot.label}
                        {timeSlot.status === STATUS.DISABLE && <STATUS_ICONS.ADD />}
                        {timeSlot.status === STATUS.AVAILABLE && <STATUS_ICONS.AVAILABLE />}
                        {timeSlot.status === STATUS.BOOKED && <STATUS_ICONS.BOOKED />}
                        {timeSlot.status === STATUS.LOCKED && <STATUS_ICONS.LOCKED />}
                      </Box>
                    </Button>
                  </Grid>
                ))
              )}
            </Grid>
          </FormControl>
        </Grid>

        {/* Submit Button */}
        <Divider sx={{ my: 2 }} />
        <Grid>
          <Button
            type="submit"
            variant="contained"
            size="large"
            fullWidth
            disabled={isSubmitting}
            onClick={handleSubmitClick}
            sx={{
              position: 'relative',
            }}
          >
            {isSubmitting ? (
              <>
                <CircularProgress
                  size={24}
                  sx={{
                    position: 'absolute',
                    left: '50%',
                    marginLeft: '-12px',
                  }}
                />
                <Box component="span" sx={{ visibility: 'hidden' }}>
                  Saving...
                </Box>
              </>
            ) : (
              'Check Your Modifications'
            )}
          </Button>
        </Grid>
      </CardContent>
    </Card>
  )
}

/**
 * Component for creating private sessions
 * Uses the usePrivateSessionCreation hook for business logic
 * Handles UI rendering and user interactions
 */
export default function PrivateSessionCreate() {
  return (
    <Box sx={{ p: 3 }}>
      {/* Confirmation Dialog */}
      <ModifyDialog />

      <Typography variant="h4" gutterBottom>
        Manage Your Private Sessions
      </Typography>

      <Box component="form">
        <Grid container justifyContent="center" spacing={2}>
          {/* Left Column */}
          <Grid size={{ xs: 12, sm: 12, md: 6, lg: 6 }} maxWidth={600}>
            <CleanPrivateSessionForm />
          </Grid>

          {/* Right Column */}
          <Grid size={{ xs: 12, sm: 12, md: 6, lg: 6 }} maxWidth={550}>
            <TimeSlotManagement />
          </Grid>
        </Grid>
      </Box>
    </Box>
  )
}
